import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scroll, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { SemearFontStyle, YHUHName, YHUSHAName } from "../components/SemearNames";

export default function Constitution() {
  return (
    <div className="min-h-screen" data-testid="constitution-page">
      <SemearFontStyle />

      {/* Hero */}
      <section className="bg-royal-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/governance"
            className="inline-flex items-center text-slate-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Governance
          </Link>

          <h1 className="heading-hero">Constitution &amp; Internal Covenant</h1>

          <p className="text-slate-300 mt-4">
            The supreme law and tribal compact of The Kingdom of Yunah'Shach'Qal Yashar'al.
          </p>
        </div>
      </section>

      {/* Document Content */}
      <section className="bg-parchment py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Document Integrity Note */}
          <div className="bg-white border-l-4 border-kingdom-gold p-4 mb-8 rounded-sm">
            <p className="text-sm text-slate-600">
              <strong>Document Integrity:</strong> Presented from the official Constitution and Internal Covenant; formatting improvements only for website readability.
            </p>
          </div>

          <div className="bg-white shadow-xl rounded-sm p-8 md:p-12">
            {/* Constitution */}
            <div id="constitution" className="mb-16">
              <div className="text-center mb-12">
                <Scroll className="w-12 h-12 text-kingdom-gold mx-auto mb-4" />

                <h2 className="font-heading text-3xl md:text-4xl font-bold text-royal-blue">
                  THE CONSTITUTION
                </h2>

                <h3 className="font-heading text-xl text-royal-blue mt-2">
                  OF THE KINGDOM OF Yunah'Shach'Qal YAHSHAR'AL
                </h3>
              </div>

              <div className="prose prose-lg max-w-none text-slate-700 space-y-6 governance-text">
                <h4 className="font-heading text-xl font-bold text-royal-blue">
                  Preamble
                </h4>

                <p>
                  We the people of Yunah'Shach'Qal Yashar'al, acknowledging <YHUHName /> Aluahym as our Supreme Ruler, Lawgiver, and Judge (Yashayahu/Isaiah 33:22), establish this Constitution to govern ourselves in righteousness, right ruling, and truth, restoring the ancient paths and walking according to the Turah of <YHUHName /> (YaHUaH) as delivered through Mashah and confirmed by the testimony of <YHUSHAName /> (YaHUSHA) Ha'Mashyach. We reject all corporate, legal fictions and statutes that oppose the laws of the Most High, and we stand as a de jure people governed by divine authority, not man-made legislations.
                </p>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Article I – Supremacy of Turah
                </h4>

                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    The Turah of <YHUHName />, as written in the five books of Mashah (Barashyt [Genesis] to Dabarym [Deuteronomy]), is the supreme law of the land.
                  </li>
                  <li>
                    No rule, custom, or policy shall be made or upheld that contradicts Turah.
                  </li>
                  <li>
                    The people shall keep the Mu'adym, dietary laws, and commandments as instructed.
                  </li>
                </ol>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Article II – The Structure of Government
                </h4>

                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    yaramYaHu and shaba Sharah are spiritual overseers, not rulers by coercion. They lead by example, wisdom, and righteous counsel.
                  </li>
                  <li>
                    The Elder Council (Zaqanym) shall be composed of wise, Turah-keeping men and women chosen by the people based on reputation, integrity, and discernment (Shamut/Exodus 18:21).
                  </li>
                  <li>
                    Tribal Representatives may be established for each extended family or grouping, ensuring accountability and fair voice in communal decisions.
                  </li>
                </ol>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Article III – Rights and Duties of the People
                </h4>

                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    Each YaSHaRUN is a free man or woman under <YHUHName />, not property of any government or corporation.
                  </li>
                  <li>
                    No YaSHaRUN shall be compelled to participate in systems of registry, licensure, taxation, or identification that bind them under Babylonian legal systems.
                  </li>
                  <li>
                    All families will train their offspring in Turah, survival, craftsmanship, and righteous community living (Deut. 6:6-9).
                  </li>
                  <li>
                    The right to land inheritance, privacy, arms for defense, and travel shall not be infringed.
                  </li>
                </ol>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Article IV – Right-Ruling and Accountability
                </h4>

                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    Right Ruling shall be executed according to righteous judgment, with at least two or three witnesses (Deut. 19:15).
                  </li>
                  <li>
                    Restitution, restoration, and Turah-based judgment shall replace prison-centered punishment, with each matter weighed by elders, judges, and lawful witnesses according to the nature of the wrong.
                  </li>
                  <li>
                    The community shall practice shalum restoration, MattitYahu (Matthew) 18 protocol, before resorting to public judgment.
                  </li>
                  <li>
                    All members have the right to speak, appeal, and be heard before any ruling is issued.
                  </li>
                </ol>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Article V – Economy and Stewardship
                </h4>

                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    The land shall not be sold forever but held as tribal inheritance (U'Yah'qara [Leviticus] 25:23).
                  </li>
                  <li>
                    Usury among brothers is forbidden.
                  </li>
                  <li>
                    The seventh year shall be a land Shabat and release of debts.
                  </li>
                  <li>
                    Communal wealth shall be used for the poor, the stranger, the orphan, and the widow.
                  </li>
                </ol>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Article VI – Defense and Foreign Relations
                </h4>

                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    This nation operates in shalum but reserves the right to defend our borders, families, and way of life.
                  </li>
                  <li>
                    No alliance shall be made with foreign powers that requires compromise of Turah.
                  </li>
                  <li>
                    Any outside engagement must first be examined through the lens of Scripture and communal counsel.
                  </li>
                </ol>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Article VII – Amendments and Preservation
                </h4>

                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    This Constitution may only be amended by unanimous agreement of the Elder Council and a 75% agreement of adult YaSHaRUN.
                  </li>
                  <li>
                    No amendment shall contradict Turah.
                  </li>
                  <li>
                    This document is to be read publicly every Sakut and available in written and oral form.
                  </li>
                </ol>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t-4 border-kingdom-gold my-12"></div>

            {/* Internal Covenant */}
            <div id="covenant">
              <div className="text-center mb-12">
                <Scroll className="w-12 h-12 text-kingdom-gold mx-auto mb-4" />

                <h2 className="font-heading text-3xl md:text-4xl font-bold text-royal-blue">
                  THE INTERNAL COVENANT
                </h2>

                <h3 className="font-heading text-xl text-royal-blue mt-2">
                  Tribal Compact of the People
                </h3>
              </div>

              <div className="prose prose-lg max-w-none text-slate-700 space-y-6 governance-text">
                <p className="text-lg font-semibold">
                  We, the people of Yunah'Shach'Qal Yashar'al, do make the following declaration before ha'shamaym and ha'aratz:
                </p>

                <ol className="list-decimal pl-6 space-y-4">
                  <li>
                    We belong to <YHUHName /> Aluahym, and no other sovereign, power, or government shall claim authority over our bodies, offspring, or souls.
                  </li>
                  <li>
                    We renounce Babylon, its idols, its feast days, including birthdays, its legal fictions, its currency systems, its statutes that oppose Turah, and any form of enslavement—whether physical, mental, or spiritual.
                  </li>
                  <li>
                    We covenant together as a qadashym, committing ourselves to one another in love, accountability, mutual aid, and being qadash.
                  </li>
                  <li>
                    We bind ourselves to Turah—to walk in righteousness, teach our offspring the Way, and build a nation that reflects the Kingdom of Ha'shamaym on ha'aratz.
                  </li>
                  <li>
                    We recognize our tribal structure, with each family a sovereign unit under <YHUHName />, yet joined together in unity as one people, one body, one House.
                  </li>
                  <li>
                    We pledge to settle disputes among ourselves according to Turah, not in the courts of the nations.
                  </li>
                  <li>
                    We proclaim freedom to the captives, healing for the brokenhearted, and right ruling for the oppressed.
                  </li>
                  <li>
                    We agree to walk in compassion, strength, and discipline, raising our sons as warriors of light and our daughters as builders of the house.
                  </li>
                  <li>
                    We declare this Covenant before witnesses, sealed by our word and upheld by our walk.
                  </li>
                </ol>

                <div className="bg-parchment p-6 rounded-sm border-l-4 border-kingdom-gold mt-8">
                  <p className="text-center italic">
                    Signed this day, in the year of Yah's calendar ________________ / Gregorian date ________________
                    <br />
                    By the hands and hearts of the founding families of Yunah'Shach'Qal Yashar'al.
                  </p>
                </div>

                <h4 className="font-heading text-xl font-bold text-royal-blue mt-8">
                  Self-Authentication and Witness Attestation
                </h4>

                <p>
                  I, ____________________________________, of the family ____________________________________, being of sound mind, full awareness, and having reached the age of majority, do freely place my autograph upon this Constitution as a living witness before <YHUHName />.
                </p>

                <p>
                  I affirm that I enter this covenantal body knowingly, willingly, and without compulsion, fraud, threat, coercion, or hidden contract.
                </p>

                <p>
                  I affirm that my first and highest allegiance is to <YHUHName />, His Turah, His righteous order, and His appointed Way, and not to any foreign power, corporate fiction, man-made status, or system that stands against Turah.
                </p>

                <p>
                  I affirm that I am a living man or living woman, not a created fiction, not a franchise, not a surety for another, and not acting as an agent of any foreign jurisdiction.
                </p>

                <p>
                  I affirm that I shall walk uprightly among the people, respect and uphold covenant order, respect the elders and council, make restitution when wrong is done, refuse oppression, refuse false witness, refuse theft, refuse bloodshed, and seek righteous judgment according to Turah.
                </p>

                <p>
                  I affirm that I am not bound to this covenant by force, but by willing agreement, and that any matter concerning discipline, correction, restoration, or removal shall be weighed by elders, judges, and council according to Turah, lawful witnesses, and righteous judgment.
                </p>

                <p>
                  This autograph is my living mark, made by my own hand, as witness of my agreement with the Constitution of Yunah'Shach'Qal Yashar'al.
                </p>
              </div>
            </div>

            {/* Download Button */}
            <div className="mt-12 pt-8 border-t border-slate-200 text-center">
              <a href="/documents/constitution.pdf" download>
                <Button className="btn-gold">
                  <Download className="w-4 h-4 mr-2" />
                  Download Official Constitution
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
